/**
 * Product section read contracts — RO-3-05 / #44298.
 *
 * The six pages in `docs/rollover/inventory/pages.md` § Product:
 *   product/page.tsx (+ _dashboard)  → product.dashboard
 *   product/[...segments]            → no new op (ModeSectionRoute + modes.*)
 *   product/o/[uid]                  → pure redirect (RO-3-14)
 *   properties/page.tsx              → product.properties
 *   properties/new/page.tsx          → product.kindOptions (+ projectActions.tracked)
 *   properties/unattributed          → product.unattributed (+ properties.reclassify)
 *
 * Every op is GET / READ. The two writes the pages used (createTrackedProperty,
 * reclassifyUnattributedAction) already mount under other namespaces — do not
 * re-mount them here.
 */

import { z } from "zod";
import type { Operation } from "../types";

// ── product.dashboard ───────────────────────────────────────────────────────
// Port of `excellent/app/product/_dashboard.tsx` — the Delivery ledger + Issues
// slice the Product mode home renders in ModePage's dashboardSlot.

const dashIssue = z.object({
  id: z.number(),
  title: z.string(),
  projectSlug: z.string(),
  due: z.string().nullable(),
});
export type ProductDashIssue = z.infer<typeof dashIssue>;

const dashProject = z.object({
  slug: z.string(),
  name: z.string(),
  open: z.number(),
  doing: z.number(),
  review: z.number(),
  lastShip: z.string().nullable(),
});
export type ProductDashProject = z.infer<typeof dashProject>;

/** Lean ScopedTask fields the Issues list + WorkRow adapter need. */
const dashTask = z.object({
  id: z.number(),
  uid: z.string().optional(),
  title: z.string(),
  status: z.string(),
  priority: z.string(),
  due_date: z.string().nullable(),
  updated_at: z.string(),
  assignee: z.string().nullable(),
  assignee_label: z.string().nullable().optional(),
  project_slug: z.string(),
  project_name: z.string(),
  project_color: z.string().nullable(),
  section_slug: z.string(),
  section_name: z.string(),
});
export type ProductDashTask = z.infer<typeof dashTask>;

const dashboardResponse = z.object({
  shippedThisWeek: z.number(),
  shippedPrevWeek: z.number(),
  shippedDelta: z.number(),
  inReview: z.number(),
  shippedRows: z.array(dashIssue),
  reviewRows: z.array(dashIssue),
  projectRows: z.array(dashProject),
  taskRows: z.array(dashTask),
});
export type ProductDashboardResponse = z.infer<typeof dashboardResponse>;

const dashboardOp: Operation<void, void, void, ProductDashboardResponse> = {
  method: "GET",
  path: () => "/api/product/dashboard",
  response: dashboardResponse,
};

// ── product.properties ──────────────────────────────────────────────────────
// Port of `excellent/app/properties/page.tsx` — the tracked-products directory.

const propertyRow = z.object({
  slug: z.string(),
  name: z.string(),
  kindLabel: z.string(),
  adoption: z.string(),
  href: z.string(),
});
export type ProductPropertyRow = z.infer<typeof propertyRow>;

const propertiesResponse = z.object({
  rows: z.array(propertyRow),
});
export type ProductPropertiesResponse = z.infer<typeof propertiesResponse>;

const propertiesOp: Operation<void, void, void, ProductPropertiesResponse> = {
  method: "GET",
  path: () => "/api/product/properties",
  response: propertiesResponse,
};

// ── product.unattributed ────────────────────────────────────────────────────
// Port of `excellent/app/properties/unattributed/page.tsx` — U6 inbox.

const unattributedRow = z.object({
  table: z.string(),
  kind: z.string(),
  rowId: z.number(),
  label: z.string(),
  via: z.string().nullable(),
  pending: z.boolean(),
  createdAt: z.string().nullable(),
});
export type ProductUnattributedRow = z.infer<typeof unattributedRow>;

const unattributedResponse = z.object({
  rows: z.array(unattributedRow),
  properties: z.array(z.object({ uid: z.string(), name: z.string() })),
});
export type ProductUnattributedResponse = z.infer<typeof unattributedResponse>;

const unattributedOp: Operation<void, void, void, ProductUnattributedResponse> = {
  method: "GET",
  path: () => "/api/product/unattributed",
  response: unattributedResponse,
};

// ── product.kindOptions ─────────────────────────────────────────────────────
// Port of the pure kind catalog `properties/new/page.tsx` derived from
// PROPERTY_KINDS + profileForKind. Server-side so the client never imports the
// core barrel (RO-4-04).

const kindMetric = z.object({
  metric: z.string(),
  label: z.string(),
  unit: z.string(),
});
const kindOption = z.object({
  value: z.string(),
  label: z.string(),
  metrics: z.array(kindMetric),
});
export type ProductKindOption = z.infer<typeof kindOption>;

const kindOptionsResponse = z.object({
  kinds: z.array(kindOption),
});
export type ProductKindOptionsResponse = z.infer<typeof kindOptionsResponse>;

const kindOptionsOp: Operation<void, void, void, ProductKindOptionsResponse> = {
  method: "GET",
  path: () => "/api/product/kind-options",
  response: kindOptionsResponse,
};

export const product = {
  dashboard: dashboardOp,
  properties: propertiesOp,
  unattributed: unattributedOp,
  kindOptions: kindOptionsOp,
};
